import {PanelLoader} from '../models/panel-loader';
import {MixedPanel} from '../models/mixed-panel';
import {BackbaseLauncherDeckContainer} from '../models/backbase-launcher-deck-container';
import {SimpleHttp} from '../../simple-http';
import {EvoGenericWidget} from '../models/evo-generic-widget';
import {Page} from '../models/page';
import {EvoWidgetBootstrapService} from './evo-widget-bootstrap.service';
import {PanelHash} from '../models/panel-hash';

export class EvoPanelLoadService implements PanelLoader {

    private evoWidgetBootstrapService = new EvoWidgetBootstrapService();
    private panelHashObjects: PanelHash;
    private panelsToAlwaysLoaded: MixedPanel[];

    public async loadPanels(ldc: BackbaseLauncherDeckContainer): Promise<Array<MixedPanel>> {
        const configName = window.sessionStorage.getItem('menuConfig') || await this.fetchConfigNameFromServer();
        this.panelHashObjects = await this.fetchWidgetConfigFromServer(configName);
        const mixedPanels = this.getMixedPanels();
        this.findPanelThatAlwaysNeedsToBeLoaded(mixedPanels);
        return mixedPanels;
    }

    private findPanelThatAlwaysNeedsToBeLoaded(panels: Array<MixedPanel>) {
        this.panelsToAlwaysLoaded = [];
        panels.forEach((panel: MixedPanel) => {
            if (panel.alwaysVisible) {
                this.panelsToAlwaysLoaded.push(panel);
            }
        });
    }

    public getPanelToAlwaysLoad(): MixedPanel[] {
        return this.panelsToAlwaysLoaded;
    }

    private getMixedPanels(): Array<MixedPanel> {
        const mixedPanels: Array<MixedPanel> = [];
        for (let [hash, evoPanel] of Object.entries(this.panelHashObjects)) {
            const self = this;
            mixedPanels.push({
                id: this.convertPanelHashToPanelId(hash),
                hash: hash,
                widgets: evoPanel.widgets,
                alwaysVisible: evoPanel.alwaysVisible,
                show: function () {
                    this.widgets.map(((widgetToShow: EvoGenericWidget) => {
                        // Note: this happens asynchronously
                        self.ensureWidgetIsLoaded(widgetToShow, hash);
                    }));
                    self.showPanelByHash(hash);
                },
                hide: () => {
                    this.hideAllPanels();
                },
                defaultPanelToShow: () => {
                    return evoPanel.showPanelByDefault ? true : false;
                }

            });
        }
        return mixedPanels;
    }

    // TODO: Improvement: we know which widgets to show, so maybe not necessary to do a document query...
    private showPanelByHash(hash: string) {
        const panels = document.getElementsByClassName('db-container-panel');
        for (let i = 0; i < panels.length; i++) {
            const item = panels.item(i) as HTMLElement;
            if ((item.attributes as any)['panel'].nodeValue === hash) {
                item.style.cssText = '';
            } else {
                item.style.cssText = 'display: none';
            }
        }
    }

    // TODO: Improvement: hide only the single panel that needs hiding
    private hideAllPanels() {
        const panels = document.getElementsByClassName('db-container-panel');
        for (let i = 0; i < panels.length; i++) {
            const item = panels[i] as HTMLElement;
            item.style.cssText = 'display: none';
        }
    }


    private ensureWidgetIsLoaded(widget: EvoGenericWidget, hash: string) {
        // Check if widget is not loaded in the selected panel
        const panelElement = document.getElementById(this.convertPanelHashToPanelId(hash));
        if (!panelElement || (panelElement && panelElement.getElementsByClassName(widget.id).length === 0)) {
            const widgetElementWrapper = this.createWidgetDOMElements(widget, hash);
            this.loadWidgetScripts(widget, widgetElementWrapper);
        }
    }

    private loadWidgetScripts(widget: EvoGenericWidget, elementWrapper: HTMLElement) {
        this.evoWidgetBootstrapService.bootstrapWidget(widget, elementWrapper);
    }

    private convertPanelHashToPanelId(hash: string) {
        return 'panel-' + hash.replace(/[#\/]/g, '');
    }

    private createWidgetDOMElements(widget: EvoGenericWidget, hash: string): HTMLElement {
        let container = document.getElementsByTagName('db-widget-container')[0];
        if (!container) {
            const backbaseMainPanel = document.querySelectorAll('.lp-lc--main')[0];
            container = document.createElement('db-widget-container');
            backbaseMainPanel.appendChild(container);
        }


        //  const panelId = widget.panelId || this.convertPanelHashToPanelId(widget.panel);
        const panelId = this.convertPanelHashToPanelId(hash);
        let panelElement: HTMLElement = document.getElementById(panelId);
        if (!panelElement) {
            panelElement = document.createElement('div');
            panelElement.setAttribute('id', panelId);
            panelElement.setAttribute('class', 'db-container-panel');
            panelElement.setAttribute('panel', hash);
        }

        const widgetWrapperElement = document.createElement('div');
        const widgetElement = document.createElement('db-generic-widget');
        Object.defineProperty(widgetElement, 'widget', {
            enumerable: false,
            configurable: false,
            writable: false,
            value: widget
        });

        widgetWrapperElement.setAttribute('class', `${widget.id} db-container-widget`);
        widgetWrapperElement.setAttribute('order', widget.order && widget.order.toString() || '0');

        widgetWrapperElement.appendChild(widgetElement);
        if (panelElement.children.length <= 0) {
            panelElement.appendChild(widgetWrapperElement);
        } else {
            for (let i = 0; i < panelElement.children.length; i++) {
                if (panelElement.children[i].getAttribute('order') && parseInt(panelElement.children[i].getAttribute('order')) > widget.order) {
                    panelElement.insertBefore(widgetWrapperElement, panelElement.children[i]);
                } else {
                    panelElement.appendChild(widgetWrapperElement);
                }
            }
        }
        container.appendChild(panelElement);

        return widgetWrapperElement;

    }

    private getConfigNameBasedOnCurrentContextPath() {
        const pathnameElements = window.location.pathname.split('/');
        const pageName = pathnameElements[pathnameElements.length - 1];

        let applicationType = 'mifid';
        if (pageName === 'euqt') {
            applicationType = 'euqt';
        }

        return applicationType + '-panel';
    }

    private async fetchConfigNameFromServer() {
        return new Promise<string>(((resolve) => {
            SimpleHttp('GET', '/widgets/config/config.json', (config: any) => {
                resolve(config.configName);
            });
        }));
    }

    private async fetchWidgetConfigFromServer(configName: string) {
        return new Promise<PanelHash>(((resolve) => {
            const currentConfigNameBasedOnContextPath = this.getConfigNameBasedOnCurrentContextPath();
            SimpleHttp('GET', '/widgets/config/' + currentConfigNameBasedOnContextPath + '-' + configName + '.json', (config: Page) => {
                resolve(this.getPageSpecificPanels(config));
            });
        }));
    }

    private getPageSpecificPanels(pageConfig: Page): PanelHash {
        const pathname = window.location.pathname;
        return pathname.indexOf('ssologin') > -1 ? pageConfig.login : pageConfig.olbPortal;
    }

}
